import React from 'react';
import { createPortal } from 'react-dom';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api, getApiErrorMessage } from '../api';
import { getSettings } from '../settings';
import StorageUsageBar from './StorageUsageBar';
import FolderItemGrid from './FolderItemGrid';
import SocialLibrary from './SocialLibrary';
import Digipals from './Digipals';
import TreeFolderNode from './TreeFolderNode';
import RolodexFrame from './RolodexFrame';
import ItemDetails from './ItemDetails';

const EXPANSION_KEY = 'digidex.library.expanded';
const VIEW_KEY = 'digidex.library.view';
const SCOPE_KEY = 'digidex.library.scope';
const libraryCaches = new Map();

function getLibraryCache() {
  const key = localStorage.getItem('token') || 'anonymous';
  if (!libraryCaches.has(key)) libraryCaches.set(key, { tree: null, tagOptions: [], folderData: {} });
  return libraryCaches.get(key);
}

function labelCutStyle(seedText) {
  let seed = 0;
  for (let index = 0; index < seedText.length; index += 1) seed = (seed * 31 + seedText.charCodeAt(index)) >>> 0;
  const next = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  const textureX = `${(20 + next() * 60).toFixed(1)}%`;
  return {
    '--label-x': textureX,
  };
}

function Library({ initialScope }) {
  const libraryCacheRef = React.useRef(null);
  if (!libraryCacheRef.current) libraryCacheRef.current = getLibraryCache();
  const libraryCache = libraryCacheRef.current;
  const location = useLocation();
  const [routeParams] = useSearchParams();
  const normalizeScope = value => value === 'pals' ? 'saved' : value;
  const [scope, setScope] = React.useState(() => normalizeScope(initialScope || routeParams.get('scope') || localStorage.getItem(SCOPE_KEY) || 'my'));
  const [expanded, setExpanded] = React.useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(EXPANSION_KEY) || '[]')); } catch { return new Set(); }
  });
  const [tree, setTree] = React.useState(() => libraryCache.tree || { untaggedCount: 0, tags: [] });
  const [folderData, setFolderData] = React.useState(() => libraryCache.folderData);
  const folderDataRef = React.useRef(libraryCache.folderData);
  const [tagOptions, setTagOptions] = React.useState(() => libraryCache.tagOptions);
  const [selectedTags, setSelectedTags] = React.useState([]);
  const [searchMode, setSearchMode] = React.useState('and');
  const [loadingTree, setLoadingTree] = React.useState(() => !libraryCache.tree);
  const [error, setError] = React.useState('');
  const [tagManagerOpen, setTagManagerOpen] = React.useState(false);
  const [tagManagerExpanded, setTagManagerExpanded] = React.useState(new Set());
  const [tagName, setTagName] = React.useState('');
  const [tagParent, setTagParent] = React.useState('');
  const [tagBusy, setTagBusy] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState(null);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [settings, setSettings] = React.useState(getSettings);
  const [organizeMode, setOrganizeMode] = React.useState(false);
  const [selectedItemIds, setSelectedItemIds] = React.useState(new Set());
  const [bulkTagsOpen, setBulkTagsOpen] = React.useState(false);
  const [bulkSelectedTags, setBulkSelectedTags] = React.useState([]);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [success, setSuccess] = React.useState('');
  const [viewMode, setViewMode] = React.useState(() => localStorage.getItem(VIEW_KEY) || 'tree');
  const [gallerySort, setGallerySort] = React.useState('newest');
  const [usage, setUsage] = React.useState(null);
  const [visibilityTarget, setVisibilityTarget] = React.useState(null);
  const [socialSearchQuery, setSocialSearchQuery] = React.useState('');
  const [socialSearchMode, setSocialSearchMode] = React.useState('and');
  const [, setSocialSearchOptions] = React.useState([]);
  const [socialSelectedTags, setSocialSelectedTags] = React.useState([]);
  const [scopeDirection, setScopeDirection] = React.useState('next');
  const [openItem, setOpenItem] = React.useState(null);
  const navigate = useNavigate();

  const changeScope = React.useCallback((nextScope, direction) => {
    setScopeDirection(direction || 'next');
    setOpenItem(null);
    setSocialSearchQuery('');
    setSocialSearchMode('and');
    setSocialSelectedTags([]);
    if (nextScope === 'digipals') navigate('/digipals', { replace: true });
    else if (nextScope === 'my') navigate('/', { replace: true });
    else navigate(`/?scope=${nextScope}`, { replace: true });
  }, [navigate]);

  React.useEffect(() => localStorage.setItem(SCOPE_KEY, scope), [scope]);

  React.useEffect(() => {
    const rawScope = routeParams.get('scope');
    if (rawScope === 'pals') {
      const nextParams = new URLSearchParams(location.search);
      nextParams.set('scope', 'saved');
      navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
      return;
    }
    const requestedScope = normalizeScope(rawScope || (location.pathname === '/digipals' ? 'digipals' : 'my'));
    if (requestedScope !== scope) {
      setScope(requestedScope);
      setOpenItem(null);
    }
  }, [location.pathname, location.search, navigate, routeParams, scope]);

  React.useEffect(() => {
    const frameContent = document.querySelector('.rolodex-card-content');
    if (frameContent) frameContent.scrollTop = 0;
  }, [scope]);

  React.useEffect(() => {
    api.get('/v1/storage/usage').then(({ data }) => setUsage(data)).catch(() => {});
  }, []);

  React.useEffect(() => {
    const handleSettingsChanged = event => setSettings(event.detail || getSettings());
    window.addEventListener('settings:changed', handleSettingsChanged);
    return () => window.removeEventListener('settings:changed', handleSettingsChanged);
  }, []);

  const updateFolderData = React.useCallback(updater => setFolderData(current => {
    const next = updater(current);
    folderDataRef.current = next;
    libraryCache.folderData = next;
    return next;
  }), [libraryCache]);

  const loadFolder = React.useCallback(async (key, reset = false) => {
    const current = folderDataRef.current[key] || {};
    const params = { limit: 30 };
    if (!reset && current.cursor) params.cursor = current.cursor;
    if (key === 'search' || key === 'gallery') {
      params.tagIds = selectedTags.map(tag => tag.id).join(',');
      if (selectedTags.length > 0) params.mode = searchMode;
      if (key === 'gallery') {
        params.view = 'gallery';
        params.sort = gallerySort;
      }
    } else if (key !== 'untagged') {
      params.tagId = key;
    }
    updateFolderData(previous => ({ ...previous, [key]: { ...(previous[key] || {}), loading: reset || !previous[key]?.loaded, loadingMore: !reset, error: '' } }));
    try {
      const { data } = await api.get('/v1/library', { params });
      updateFolderData(previous => ({ ...previous, [key]: { items: reset ? data.items : [...(previous[key]?.items || []), ...data.items], cursor: data.nextCursor, hasMore: data.hasMore, loaded: true, loading: false, loadingMore: false, error: '' } }));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load Library items.'));
      updateFolderData(previous => ({ ...previous, [key]: { ...(previous[key] || {}), loading: false, loadingMore: false, error: true } }));
    }
  }, [gallerySort, searchMode, selectedTags, updateFolderData]);

  const loadTree = React.useCallback(async () => {
    try {
      const [treeResponse, tagsResponse] = await Promise.all([api.get('/v1/library/tree'), api.get('/v1/tags')]);
      const cachedUntagged = folderDataRef.current.untagged;
      if (cachedUntagged?.loaded && cachedUntagged.items.length !== treeResponse.data.untaggedCount) {
        const nextFolderData = { ...folderDataRef.current };
        delete nextFolderData.untagged;
        folderDataRef.current = nextFolderData;
        setFolderData(nextFolderData);
        libraryCache.folderData = nextFolderData;
      }
      setTree(treeResponse.data);
      setTagOptions(tagsResponse.data);
      libraryCache.tree = treeResponse.data;
      libraryCache.tagOptions = tagsResponse.data;
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load Library.'));
    } finally {
      setLoadingTree(false);
    }
  }, [libraryCache]);

  React.useEffect(() => { loadTree(); }, [loadTree]);
  React.useEffect(() => {
    if (loadingTree) return;
    expanded.forEach(key => {
      if (key === 'untagged' || tree.tags.some(tag => tag.id === key)) {
        if (!folderDataRef.current[key]?.loaded) loadFolder(key, true);
      }
    });
  }, [expanded, loadingTree, loadFolder, tree.tags]);
  React.useEffect(() => {
    if (viewMode === 'gallery') loadFolder('gallery', true);
    else if (selectedTags.length > 0) loadFolder('search', true);
  }, [selectedTags, searchMode, gallerySort, viewMode, loadFolder]);

  const changeView = mode => {
    setViewMode(mode);
    localStorage.setItem(VIEW_KEY, mode);
    setSelectedItemIds(new Set());
    setSuccess('');
  };

  const toggle = id => setExpanded(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    localStorage.setItem(EXPANSION_KEY, JSON.stringify([...next]));
    return next;
  });

  const reloadTree = async () => {
    const [treeResponse, tagsResponse] = await Promise.all([api.get('/v1/library/tree'), api.get('/v1/tags')]);
    const cachedUntagged = folderDataRef.current.untagged;
    if (cachedUntagged?.loaded && cachedUntagged.items.length !== treeResponse.data.untaggedCount) {
      const nextFolderData = { ...folderDataRef.current };
      delete nextFolderData.untagged;
      folderDataRef.current = nextFolderData;
      setFolderData(nextFolderData);
      libraryCache.folderData = nextFolderData;
    }
    setTree(treeResponse.data);
    setTagOptions(tagsResponse.data);
    libraryCache.tree = treeResponse.data;
    libraryCache.tagOptions = tagsResponse.data;
  };

  const reloadLibrary = async () => {
    folderDataRef.current = {};
    setFolderData({});
    libraryCache.folderData = {};
    await reloadTree();
    if (selectedTags.length > 0) await loadFolder('search', true);
    if (viewMode === 'gallery') await loadFolder('gallery', true);
  };

  const toggleOrganizeMode = () => {
    setOrganizeMode(current => {
      if (current) setSelectedItemIds(new Set());
      return !current;
    });
    setSuccess('');
  };

  const toggleItemSelection = itemId => setSelectedItemIds(current => {
    const next = new Set(current);
    if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
    return next;
  });

  const applyBulkTags = async () => {
    const itemIds = [...selectedItemIds];
    const tagIds = bulkSelectedTags.map(tag => tag.id);
    if (itemIds.length === 0 || tagIds.length === 0) return;
    setBulkBusy(true);
    setError('');
    setSuccess('');
    try {
      await api.post('/v1/items/bulk-tags', { itemIds, tagIds });
      setBulkTagsOpen(false);
      setBulkSelectedTags([]);
      setSelectedItemIds(new Set());
      await reloadLibrary();
      setSuccess(`${itemIds.length} item${itemIds.length === 1 ? '' : 's'} organized successfully.`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to organize the selected items. Your selections are still selected.'));
    } finally {
      setBulkBusy(false);
    }
  };
  const createTag = async () => {
    setTagBusy(true);
    try {
      const parentId = tagParent || null;
      await api.post('/v1/tags', { name: tagName, parentId });
      setTagName('');
      setTagParent('');
      if (parentId) setTagManagerExpanded(current => new Set(current).add(parentId));
      await reloadLibrary();
    }
    catch (err) { setError(getApiErrorMessage(err, 'Unable to create tag.')); }
    finally { setTagBusy(false); }
  };
  const editTag = tag => setEditTarget({
    tag,
    name: tag.name,
    visibility: tag.visibility || 'Inherit',
    savePolicy: tag.savePolicy || (tag.allowDigipalSaves ? 'Digipals' : 'None'),
  });
  const persistEditTag = async target => {
    if (!target || !target.name.trim()) return;
    setTagBusy(true);
    try {
      if (target.name.trim() !== target.tag.name) await api.put(`/v1/tags/${target.tag.id}`, { name: target.name });
      await api.put(`/v1/tags/${target.tag.id}/visibility`, { visibility: target.visibility, savePolicy: target.savePolicy });
      setEditTarget(null);
      setVisibilityTarget(null);
      await reloadLibrary();
    } catch (err) { setError(getApiErrorMessage(err, 'Unable to update tag settings.')); }
    finally { setTagBusy(false); }
  };
  const deleteTag = async tag => {
    setDeleteTarget(tag);
  };
  const confirmDeleteTag = async () => {
    if (!deleteTarget) return;
    setTagBusy(true);
    try {
      await api.delete(`/v1/tags/${deleteTarget.id}`);
      setDeleteTarget(null);
      folderDataRef.current = {};
      setFolderData({});
      await reloadTree();
    }
    catch (err) { setError(getApiErrorMessage(err, 'Unable to delete tag.')); }
    finally { setTagBusy(false); }
  };

  const saveEditTag = () => {
    if (!editTarget) return;
    if (editTarget.visibility === 'Public' && (childrenByParent[editTarget.tag.id] || []).length > 0) {
      setVisibilityTarget(editTarget);
      return;
    }
    persistEditTag(editTarget);
  };

  const startCreateTag = parentId => {
    setTagParent(parentId || '');
    setTagName('');
    if (parentId) setTagManagerExpanded(current => new Set(current).add(parentId));
  };

  const toggleTagManagerNode = tagId => setTagManagerExpanded(current => {
    const next = new Set(current);
    if (next.has(tagId)) next.delete(tagId); else next.add(tagId);
    return next;
  });

  const childrenByParent = React.useMemo(() => tree.tags.reduce((map, tag) => {
    const key = tag.parentId || 'root';
    map[key] = [...(map[key] || []), tag];
    return map;
  }, {}), [tree.tags]);

  const sortedTagOptions = React.useMemo(() => [...tagOptions].sort((a, b) => (a.path || a.name).localeCompare(b.path || b.name, undefined, { sensitivity: 'base' })), [tagOptions]);
  const tagMetadataById = React.useMemo(() => new Map(tagOptions.map(tag => [tag.id, tag])), [tagOptions]);

  const renderManageTag = (tag, depth = 0) => {
    const children = childrenByParent[tag.id] || [];
    const isOpen = tagManagerExpanded.has(tag.id);
    const metadata = tagMetadataById.get(tag.id) || tag;
    return <Box key={tag.id} sx={{ position: 'relative', ml: depth > 0 ? 1 : 0, pl: depth > 0 ? 1 : 0, borderLeft: depth > 0 ? '1px solid #c7c7c7' : 'none', '&::before': depth > 0 ? { content: '""', position: 'absolute', top: 24, left: -1, width: 40, borderTop: '1px solid #c7c7c7' } : undefined }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
        <IconButton size="small" onClick={() => toggleTagManagerNode(tag.id)} sx={{ color: '#222', visibility: children.length > 0 ? 'visible' : 'hidden', backgroundColor: '#fff', '&:hover': { backgroundColor: '#fff' } }}>
          {isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>
        <FolderIcon sx={{ color: '#d8a62b' }} />
        <Typography sx={{ flex: 1 }}>{tag.name} ({tag.itemCount})</Typography>
        <Box sx={{ flex: 1, borderTop: '1px solid #e0e0e0', mx: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <IconButton size="small" onClick={() => startCreateTag(tag.id)} sx={{ color: '#222' }}><AddIcon /></IconButton>
          <Button size="small" onClick={() => editTag(metadata)} sx={{ color: '#222', fontWeight: 'bold', '&:hover': { color: '#000', backgroundColor: 'rgba(0,0,0,0.04)' } }}>Edit</Button>
          <IconButton size="small" onClick={() => deleteTag(tag)} sx={{ color: '#222' }}><DeleteForeverIcon /></IconButton>
        </Box>
      </Box>
      {isOpen && children.map(child => renderManageTag(child, depth + 1))}
    </Box>;
  };

  const renderTag = (tag, depth = 0) => {
    const children = childrenByParent[tag.id] || [];
    const hasContent = children.length > 0 || tag.itemCount > 0;
    const isOpen = expanded.has(tag.id);
    return <TreeFolderNode key={tag.id} name={tag.name} itemCount={tag.itemCount} depth={depth} isOpen={isOpen} hasContent={hasContent} onToggle={() => { setSelectedTags([]); toggle(tag.id); }}>{<><FolderItemGrid data={folderData[tag.id]} onLoadMore={() => loadFolder(tag.id)} settings={settings} organizeMode={organizeMode} selectedItemIds={selectedItemIds} onToggleItem={toggleItemSelection} onOpenItem={setOpenItem} />{children.map(child => renderTag(child, depth + 1))}</>}</TreeFolderNode>;
  };

  if (scope !== 'my') return <main className="page-shell dex-page-shell"><RolodexFrame scope={scope} direction={scopeDirection} onScopeChange={changeScope}>{scope === 'digipals' && !routeParams.get('ownerId') ? <Digipals embedded /> : <><SocialLibrary embedded embeddedScope={scope} onScopeChange={(nextScope, movement) => changeScope(nextScope, movement)} searchQuery={socialSearchQuery} searchMode={socialSearchMode} onSearchModeChange={setSocialSearchMode} onSearchOptionsChange={setSocialSearchOptions} onSearchQueryChange={setSocialSearchQuery} searchSelectedTags={socialSelectedTags} onSearchSelectedTagsChange={setSocialSelectedTags} openItem={openItem} onBackFromItem={() => setOpenItem(null)} onOpenItem={item => setOpenItem(item)} /></>}</RolodexFrame></main>;
  const searchActive = selectedTags.length > 0;
  return <main className={`page-shell dex-page-shell${organizeMode ? ' organize-active' : ''}`}>
    <RolodexFrame scope="my" direction={scopeDirection} onScopeChange={changeScope}>
    {openItem ? <ItemDetails embedded itemId={openItem.id} ownerId={openItem.ownerUserId} initialItem={openItem} backLabel="Back to My Dex" onBack={() => setOpenItem(null)} /> : loadingTree ? <Box className="dex-loading-state" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100%' }}><CircularProgress /></Box> : <>
    <Box className="dex-usage-container" sx={{ mb: 3 }}>
      <Box className="dex-usage-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        <StorageUsageBar usage={usage} compact />
      </Box>
      <Box className="dex-toolbar">
        <Box className="dex-toolbar-search">
          <Autocomplete multiple size="small" options={sortedTagOptions} value={selectedTags} onChange={(_event, values) => setSelectedTags(values)} getOptionLabel={option => option.path || option.name} isOptionEqualToValue={(option, value) => option.id === value.id} renderTags={(values, getTagProps) => values.map((option, index) => <Chip label={option.path || option.name} {...getTagProps({ index })} key={option.id} />)} renderInput={params => <TextField {...params} label="Search by tags" placeholder="Add tags" />} />
          <ToggleButtonGroup value={searchMode} exclusive onChange={(_event, value) => value && setSearchMode(value)} size="small"><ToggleButton value="and" sx={{ minWidth: 52, minHeight: 40, fontWeight: 'bold', '&.Mui-selected': { backgroundColor: '#777', color: '#fff', '&:hover': { backgroundColor: '#666' } } }}>AND</ToggleButton><ToggleButton value="or" sx={{ minWidth: 52, minHeight: 40, fontWeight: 'bold', '&.Mui-selected': { backgroundColor: '#777', color: '#fff', '&:hover': { backgroundColor: '#666' } } }}>OR</ToggleButton></ToggleButtonGroup>
          {searchActive && <Button size="small" onClick={() => setSelectedTags([])} sx={{ color: '#222', fontWeight: 'bold' }}>Clear</Button>}
        </Box>
        <Box className="dex-toolbar-actions">
          <ToggleButtonGroup className="view-mode-toggle" value={viewMode} exclusive onChange={(_event, value) => value && changeView(value)} size="small"><ToggleButton style={labelCutStyle('tree-view')} className="label-button label-button-neutral" value="tree" sx={{ minWidth: 112, fontWeight: 'bold' }}>Tree View</ToggleButton><ToggleButton style={labelCutStyle('gallery-view')} className="label-button label-button-neutral" value="gallery" sx={{ minWidth: 112, fontWeight: 'bold' }}>Gallery View</ToggleButton></ToggleButtonGroup>
          <Button style={labelCutStyle('organize')} className="label-button label-button-dark" variant="contained" startIcon={<EditIcon />} onClick={toggleOrganizeMode} sx={{ fontWeight: 'bold' }}>{organizeMode ? 'Exit Organize' : 'Organize'}</Button>{viewMode === 'tree' && <Button style={labelCutStyle('manage-tags')} className="label-button label-button-dark" variant="contained" startIcon={<AccountTreeIcon />} onClick={() => setTagManagerOpen(true)} sx={{ fontWeight: 'bold' }}>Manage Tags</Button>}
        </Box>
      </Box>
    </Box>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
    {viewMode === 'gallery' && <Card sx={{ mb: 2 }}><CardContent><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}><Typography variant="h6">{searchActive ? 'Gallery search results' : 'Gallery'}</Typography><ToggleButtonGroup value={gallerySort} exclusive onChange={(_event, value) => value && setGallerySort(value)} size="small"><ToggleButton value="newest" sx={{ fontWeight: 'bold' }}>Newest</ToggleButton><ToggleButton value="oldest" sx={{ fontWeight: 'bold' }}>Oldest</ToggleButton><ToggleButton value="updated" sx={{ fontWeight: 'bold' }}>Updated</ToggleButton></ToggleButtonGroup></Box><FolderItemGrid data={folderData.gallery} onLoadMore={() => loadFolder('gallery')} settings={settings} organizeMode={organizeMode} selectedItemIds={selectedItemIds} onToggleItem={toggleItemSelection} onOpenItem={setOpenItem} /></CardContent></Card>}
    {viewMode === 'tree' && searchActive && <Card sx={{ mb: 2 }}><CardContent><Typography variant="h6">Search results</Typography><FolderItemGrid data={folderData.search} onLoadMore={() => loadFolder('search')} settings={settings} organizeMode={organizeMode} selectedItemIds={selectedItemIds} onToggleItem={toggleItemSelection} onOpenItem={setOpenItem} /></CardContent></Card>}
    {viewMode === 'tree' && !searchActive && <Card><CardContent>
      <Button onClick={() => toggle('untagged')} color="inherit" sx={{ width: '100%', justifyContent: 'flex-start', textTransform: 'none', p: 1, gap: 0.5 }}>
        {expanded.has('untagged') ? <ExpandMoreIcon /> : <ChevronRightIcon />}<FolderIcon sx={{ color: '#d8a62b' }} /><Typography fontWeight="bold">Untagged ({tree.untaggedCount})</Typography>
      </Button>
      {expanded.has('untagged') && <FolderItemGrid data={folderData.untagged} onLoadMore={() => loadFolder('untagged')} settings={settings} organizeMode={organizeMode} selectedItemIds={selectedItemIds} onToggleItem={toggleItemSelection} onOpenItem={setOpenItem} />}
      {tree.tags.filter(tag => !tag.parentId).map(tag => renderTag(tag))}
    </CardContent></Card>}
    {organizeMode && createPortal(<Box className="organize-action-bar" sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', md: 'flex-end' }, gap: 1, p: 1.5, backgroundColor: '#fff', borderTop: '1px solid #ddd', boxShadow: '0 -2px 8px rgba(0,0,0,0.12)' }}>
      <Typography fontWeight="bold">{selectedItemIds.size} selected</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button style={labelCutStyle('add-tags')} className="label-button label-button-dark" variant="contained" disabled={selectedItemIds.size === 0} onClick={() => setBulkTagsOpen(true)} sx={{ fontWeight: 'bold' }}>Add Tags</Button>
        <Button onClick={toggleOrganizeMode} sx={{ color: '#222', fontWeight: 'bold' }}>Exit</Button>
      </Box>
    </Box>, document.body)}
    <Dialog open={bulkTagsOpen} onClose={() => !bulkBusy && setBulkTagsOpen(false)} fullWidth maxWidth="sm"><DialogTitle sx={{ fontWeight: 'bold' }}>Add Tags to {selectedItemIds.size} Item{selectedItemIds.size === 1 ? '' : 's'}</DialogTitle><DialogContent sx={{ pt: '8px !important' }}><Autocomplete multiple autoFocus options={sortedTagOptions} value={bulkSelectedTags} onChange={(_event, values) => setBulkSelectedTags(values)} getOptionLabel={option => option.path || option.name} isOptionEqualToValue={(option, value) => option.id === value.id} renderTags={(values, getTagProps) => values.map((option, index) => <Chip label={option.path || option.name} {...getTagProps({ index })} key={option.id} />)} renderInput={params => <TextField {...params} label="Tags" placeholder="Search existing tags" />} /></DialogContent><DialogActions><Button onClick={() => setBulkTagsOpen(false)} disabled={bulkBusy} sx={{ color: '#222' }}>Cancel</Button><Button onClick={applyBulkTags} variant="contained" disabled={bulkBusy || bulkSelectedTags.length === 0} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{bulkBusy ? 'Applying…' : 'Apply Tags'}</Button></DialogActions></Dialog>
    <Dialog open={Boolean(deleteTarget)} onClose={() => !tagBusy && setDeleteTarget(null)} fullWidth maxWidth="sm"><DialogTitle sx={{ fontWeight: 'bold' }}>Delete Tag?</DialogTitle><DialogContent>Deleting <strong>{deleteTarget?.name}</strong> may affect {deleteTarget?.itemCount || 0} item{deleteTarget?.itemCount === 1 ? '' : 's'}. Items will remain in other tag areas or move to Untagged.</DialogContent><DialogActions><Button onClick={() => setDeleteTarget(null)} disabled={tagBusy} sx={{ color: '#222' }}>Cancel</Button><Button onClick={confirmDeleteTag} variant="contained" disabled={tagBusy} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{tagBusy ? 'Deleting…' : 'Delete Tag'}</Button></DialogActions></Dialog>
    <Dialog open={Boolean(editTarget)} onClose={() => !tagBusy && setEditTarget(null)} fullWidth maxWidth="sm"><DialogTitle sx={{ fontWeight: 'bold' }}>Edit Tag</DialogTitle><DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}><TextField autoFocus fullWidth label="Tag name" value={editTarget?.name || ''} onChange={event => setEditTarget(current => current ? { ...current, name: event.target.value } : current)} inputProps={{ maxLength: 200 }} /><Box><Typography variant="subtitle2" fontWeight="bold">Visibility</Typography><Select fullWidth value={editTarget?.visibility || 'Inherit'} onChange={event => setEditTarget(current => current ? { ...current, visibility: event.target.value } : current)}><MenuItem value="Inherit">Inherit visibility</MenuItem><MenuItem value="Friends">Digipals only</MenuItem><MenuItem value="Public">Public</MenuItem><MenuItem value="Private">Private</MenuItem></Select><Typography variant="caption" color="text.secondary">Inherit follows the parent tag’s visibility, or your Dex visibility when there is no parent.</Typography></Box><Box><Typography variant="subtitle2" fontWeight="bold">Who can save this tag?</Typography><Select fullWidth value={editTarget?.savePolicy || 'Digipals'} onChange={event => setEditTarget(current => current ? { ...current, savePolicy: event.target.value } : current)}><MenuItem value="Anyone">Anyone can save</MenuItem><MenuItem value="Digipals">Digipals only</MenuItem><MenuItem value="None">No one can save</MenuItem></Select><Typography variant="caption" color="text.secondary">Anyone requires this tag to be publicly visible. Saving permission is separate from viewing permission.</Typography></Box></DialogContent><DialogActions><Button onClick={() => setEditTarget(null)} disabled={tagBusy} sx={{ color: '#222', fontWeight: 'bold' }}>Cancel</Button><Button onClick={saveEditTag} variant="contained" disabled={tagBusy || !editTarget?.name?.trim()} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{tagBusy ? 'Saving…' : 'Save'}</Button></DialogActions></Dialog>
    <Dialog open={tagManagerOpen} onClose={() => !tagBusy && setTagManagerOpen(false)} fullWidth maxWidth="sm"><DialogTitle sx={{ fontWeight: 'bold' }}>Manage Tags</DialogTitle><DialogContent><Box sx={{ display: 'grid', gap: 2, mb: 3, pt: 1 }}><TextField label="New tag" value={tagName} onChange={event => setTagName(event.target.value)} inputProps={{ maxLength: 200 }} /><Autocomplete options={sortedTagOptions} value={sortedTagOptions.find(tag => tag.id === tagParent) || null} onChange={(_event, value) => setTagParent(value?.id || '')} getOptionLabel={option => option.path || option.name} isOptionEqualToValue={(option, value) => option.id === value.id} renderInput={params => <TextField {...params} label="Parent tag (optional)" placeholder="Search tags" />} /><Button className="label-button label-button-dark" onClick={createTag} variant="contained" disabled={tagBusy || !tagName.trim()}>{tagBusy ? 'Creating…' : 'Create Tag'}</Button></Box><Box sx={{ borderTop: '1px solid #ddd', pt: 1 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}><Typography fontWeight="bold" sx={{ flex: 1 }}>Tag tree</Typography><Button size="small" startIcon={<AddIcon />} onClick={() => setTagParent('')} sx={{ color: '#222', fontWeight: 'bold', '&:hover': { color: '#000', backgroundColor: 'rgba(0,0,0,0.04)' } }}>Root tag</Button></Box>{(childrenByParent.root || []).length === 0 ? <Typography color="text.secondary">No tags yet.</Typography> : (childrenByParent.root || []).map(tag => renderManageTag(tag))}</Box></DialogContent><DialogActions><Button onClick={() => setTagManagerOpen(false)} disabled={tagBusy} sx={{ color: '#222', fontWeight: 'bold' }}>Close</Button></DialogActions></Dialog>
    <Dialog open={Boolean(visibilityTarget)} onClose={() => !tagBusy && setVisibilityTarget(null)} fullWidth maxWidth="sm"><DialogTitle sx={{ fontWeight: 'bold' }}>Make tag public?</DialogTitle><DialogContent>Making <strong>{visibilityTarget?.tag.name}</strong> public may expose its permitted nested tags and items to signed-in Digidex users. Private descendants remain hidden, and hidden parent paths are not shown.</DialogContent><DialogActions><Button onClick={() => setVisibilityTarget(null)} disabled={tagBusy} sx={{ color: '#222', fontWeight: 'bold' }}>Cancel</Button><Button onClick={() => persistEditTag(visibilityTarget)} variant="contained" disabled={tagBusy} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{tagBusy ? 'Saving…' : 'Make Public'}</Button></DialogActions></Dialog>
    </>}
    </RolodexFrame>
  </main>;
}

export default Library;
export { FolderItemGrid };
